export { jsObjectToApi } from './jsObjectToApi';
export { default as stringBuilder } from './stringBuilder';
export { groupBy, groupByAsArray } from './group';
export { debounce } from './delay';
export { rangeNumber, rangeDate, clamp } from './range';

/**
 * @function deprecated
 * @description This function will mark a prop as deprecated.
 * @param validator A PropTypes validation
 * @example
 * static propTypes = {
 *     columns: PropTypes.arrayOf(PropTypes.shape({
 *         label: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.elementType]).isRequired,
 *         key: PropTypes.string.isRequired,
 *         className: PropTypes.string,
 *         dataClassName: PropTypes.string,
 *         translate: deprecated(PropTypes.bool), // When given, it will log a message in the console
 *         mobile: PropTypes.bool,
 *         sortable: PropTypes.bool
 *     })).isRequired,
 *     topSection: deprecated(PropTypes.elementType), // When given, it will log a message in the console
 *     topSectionProps: deprecated(PropTypes.object), // When given, it will log a message in the console
 *     renderRow: PropTypes.func,
 * }
 * @copyright Spordle
 */
export function deprecated(validator: any) {
    return function(props: any, propName: any, componentName: any, location: any, where: any, ...rest: any) {
        if (props[propName] != null) {
            console.warn(`${componentName}: ${propName} ${where ? `at ${where} ` : ''}is deprecated. It will be remove in the next Major update. See the documentation for more info.`);
        }
        return validator(props, propName, componentName, location, where, ...rest);
    }
}

declare global {
    interface Array<T> {
        /**
         * Function that can be called to flatten the array into a 1 level array.
         * @param {string} subArrayIndex The object's index you want to iterate recursivelly
         * @param {function} buildItemCallback This is how to build the items inside the array
         * @returns {Array} The 1 level array
         * @example
         * organizations.flattenArray('sub_organisations', (org) => ({
         *     id: org.organisation_id,
         *     value: org.organisation_id,
         *     label: org.organisation_name
         * }))
         * @copyright Spordle
         */
        flattenArray<S>(subArrayIndex: string, buildItemCallback: (currentItem: T) => S): S[];
        /**
         * Function that will append an array to the current array
         * @param {Array} arr The array to append to the array
         * @returns {Number} The new array length
         * @example
         * const myArray = ['1', '2'];
         * myArray.pushArray(['a', 'b'])
         *
         * @copyright Spordle
         */
        pushArray(arr: T[]): number;
        /**
         * Function that will add the given elements starting at the given position
         * @param {number} at Where to insert the [newElements]
         * @param {any} newElements The element(s) to insert in the array
         * @example
         * const testArray = ['1', '2', '5'];
         * testArray.insertAt(2, ['3', '4']);
         * testArray.insertAt(0, 'test');
         * console.log(testArray); -> ['test', '1', '2', '3', '4', '5']
         *
         * @copyright Spordle
         */
        insertAt(at: number, newElements: T|T[]): void
        /**
         * Function that will remove duplicate values from the array
         * @param {Function?} getKey
         * @example
         * ['1', '2', '5', '5', '5', '1'].removeDuplicates();
         *
         * @copyright Spordle
         */
        removeDuplicates(getKey?: (item: T) => any): T[]
    }
}

/**
 * @description
 * Function that can be called to flatten the array into a 1 level array.<br/>
 * This function is an array prototype <code>Array.prototype.flattenArray</code>
 * @typedef flattenArray
 * @function flattenArray
 * @param {string} subArrayIndex The object's index you want to iterate recursivelly
 * @param {function} buildItemCallback This is how to build the items inside the array
 * @example
 * organizations.flattenArray('sub_organisations', (org) => ({
 *     id: org.organisation_id,
 *     value: org.organisation_id,
 *     label: org.organisation_name
 * }))
 *
 * @returns {Array} The 1 level array
 * @copyright Spordle
 */

/**
 * @description
 * Function that will append an array to the current array.<br/>
 * This function is an array prototype <code>Array.prototype.pushArray</code>
 * @typedef pushArray
 * @function pushArray
 * @param {Array} arr The array to append to the array
 * @example
 * const myArray = ['1', '2'];
 * myArray.pushArray(['a', 'b'])
 *
 * @returns {Number} The new array length
 * @copyright Spordle
 */

/**
 * @description
 * Function that will add the given elements starting at the given position.<br/>
 * This function is an array prototype <code>Array.prototype.insertAt</code>
 * @typedef insertAt
 * @function insertAt
 * @param {number} at Where to insert the <code>newElements</code>
 * @param {any} newElements The element(s) to insert in the array
 * @example
 * const testArray = ['1', '2', '5'];
 * testArray.insertAt(2, ['3', '4']);
 * testArray.insertAt(0, 'test');
 * console.log(testArray); -> ['test', '1', '2', '3', '4', '5']
 *
 * @copyright Spordle
 */
/**
 * @description
 * Function that will remove duplicate values from the array.<br/>
 * This function is an array prototype <code>Array.prototype.removeDuplicates</code>
 * @typedef removeDuplicates
 * @function removeDuplicates
 * @param {Function?} getKey
 * @example
 * ['1', '2', '5', '5', '5', '1'].removeDuplicates();
 *
 * @copyright Spordle
 */

Object.defineProperties<any[]>(Array.prototype, {
    insertAt: {
        value: function(at: number, newElements: any){
            if(Array.isArray(newElements))
                this.splice(at, 0, ...newElements);
            else
                this.splice(at, 0, newElements);
        },
        configurable: true,
    },
    flattenArray: {
        value: function(subArrayIndex: string, buildItemCallback: Function){
            return this.reduce((buildedArray: any[], currentItem: any) => {
                buildedArray.push(buildItemCallback(currentItem));
                if(currentItem[subArrayIndex]){
                    buildedArray.pushArray(currentItem[subArrayIndex].flattenArray(subArrayIndex, buildItemCallback));
                }
                return buildedArray;
            }, []);
        },
        configurable: true,
    },
    pushArray: {
        value: function(arr: any[]){
            const initialLength = this.length;
            this.length += arr.length;

            for(var i = 0; i < arr.length; i++)
                this[initialLength + i] = arr[i];

            return this.length;
        },
        configurable: true,
    },
    removeDuplicates: {
        value: function(getKey?: (item: any) => any) {
            if(!getKey)
                return Array.from(new Set(this));

            const seen = new Set<any>();
            return this.filter((item: any) => {
                const k = getKey(item);
                return seen.has(k) ? false : seen.add(k);
            });
        },
        configurable: true,
    },
});